import { FC } from 'react';
import { DrlResult as DrlResultData, DrlResultItem, DrlStatus } from '@/types/assistant';

interface Props {
  result: DrlResultData;
}

interface StatusMeta {
  icon: string;
  label: string;
  /** Cor do ícone/realce do status. */
  tone: string;
}

const STATUS_META: Record<DrlStatus, StatusMeta> = {
  encontrado: { icon: 'check_circle', label: 'Encontrado', tone: 'text-emerald-400' },
  revisar: { icon: 'help', label: 'Revisar', tone: 'text-amber-400' },
  nao_encontrado: { icon: 'cancel', label: 'Faltando', tone: 'text-red-400' },
};

/** Badge de confiança (NN%) — tom acompanha o status. */
const ConfidenceBadge: FC<{ value: number; status: DrlStatus }> = ({ value, status }) => (
  <span
    data-testid="drl-confidence"
    className={`shrink-0 text-2xs font-semibold px-1.5 py-0.5 rounded-md bg-surface-tertiary border border-surface-border ${STATUS_META[status].tone}`}
    title="Confiança da localização automática"
  >
    {value}%
  </span>
);

/** Chip de link pro arquivo no SharePoint (abre em nova aba). */
const FileLink: FC<{ name: string; webUrl: string; score?: number | null }> = ({ name, webUrl, score }) => (
  <a
    data-testid="drl-file-link"
    href={webUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 max-w-full text-xs px-2.5 py-1 rounded-lg bg-surface-tertiary border border-surface-border text-content-secondary hover:border-accent hover:text-accent transition"
    title={`Abrir "${name}" no SharePoint`}
  >
    <span className="material-symbols-outlined text-sm leading-none">description</span>
    <span className="truncate">{name}</span>
    {typeof score === 'number' && <span className="text-content-tertiary shrink-0">· {score}%</span>}
    <span className="material-symbols-outlined text-sm leading-none shrink-0">open_in_new</span>
  </a>
);

const DrlItemRow: FC<{ row: DrlResultItem }> = ({ row }) => {
  const meta = STATUS_META[row.status] ?? STATUS_META.nao_encontrado;
  const best = row.candidatos[0];
  return (
    <li
      data-testid="drl-item"
      data-status={row.status}
      className="py-2.5 first:pt-0 last:pb-0 border-b border-surface-border last:border-0"
    >
      <div className="flex items-start gap-2">
        <span
          className={`material-symbols-outlined text-lg leading-none mt-0.5 shrink-0 ${meta.tone}`}
          aria-hidden
        >
          {meta.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {row.item && <span className="text-2xs text-content-tertiary shrink-0">#{row.item}</span>}
            <span className="text-sm text-content-primary font-medium break-words">{row.descricao}</span>
            <span className={`text-2xs font-semibold ${meta.tone}`}>{meta.label}</span>
            {row.status !== 'nao_encontrado' && (
              <ConfidenceBadge value={row.confianca} status={row.status} />
            )}
          </div>

          {/* Encontrado → 1 link direto. */}
          {row.status === 'encontrado' && best && (
            <div className="mt-1.5">
              <FileLink name={best.name} webUrl={best.webUrl} />
            </div>
          )}

          {/* Revisar → candidatos prováveis (a pessoa confirma qual é). */}
          {row.status === 'revisar' && row.candidatos.length > 0 && (
            <div className="mt-1.5">
              <p className="text-2xs uppercase tracking-wide text-content-tertiary mb-1">
                Candidatos — confira qual é
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="drl-candidates">
                {row.candidatos.map((c, i) => (
                  <FileLink key={`${c.webUrl}:${i}`} name={c.name} webUrl={c.webUrl} score={c.score} />
                ))}
              </div>
            </div>
          )}

          {row.porque && <p className="mt-1 text-2xs text-content-tertiary">{row.porque}</p>}
        </div>
      </div>
    </li>
  );
};

/**
 * Resultado estruturado do matching do DRL (Frente 5, S06). Renderizado abaixo da
 * resposta do H.Find a partir de `Message.metadata.drl_result` (S04). Mostra o resumo
 * ("X de Y encontrados"), a lista de itens com status/confiança/links e o disclaimer.
 */
const DrlResult: FC<Props> = ({ result }) => {
  const { resumo, itens } = result;
  return (
    <div
      data-testid="drl-result"
      className="mt-3 pt-3 border-t border-surface-border"
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="material-symbols-outlined text-accent text-lg leading-none">fact_check</span>
        <span data-testid="drl-summary" className="text-sm font-semibold text-content-primary">
          {resumo.encontrados} de {resumo.total} encontrados
        </span>
        {resumo.revisar > 0 && (
          <span className="text-xs text-amber-400">· {resumo.revisar} revisar</span>
        )}
        {resumo.faltando > 0 && (
          <span className="text-xs text-red-400">· {resumo.faltando} faltando</span>
        )}
        {result.source_file && (
          <span className="text-2xs text-content-tertiary truncate">· {result.source_file}</span>
        )}
      </div>

      <ul className="rounded-lg bg-surface-secondary border border-surface-border px-3 py-2">
        {itens.map((row, i) => (
          <DrlItemRow key={`${row.item}:${i}`} row={row} />
        ))}
      </ul>

      {result.disclaimer && (
        <p className="mt-2 text-2xs text-content-tertiary flex items-start gap-1">
          <span className="material-symbols-outlined text-amber-400 text-sm leading-none shrink-0">info</span>
          <span>{result.disclaimer}</span>
        </p>
      )}
    </div>
  );
};

export default DrlResult;
